/**
 * Copyright Open Logistics Foundation
 *
 * Licensed under the Open Logistics Foundation License 1.3.
 * For details on the licensing terms, see the LICENSE file.
 * SPDX-License-Identifier: OLFL-1.3
 */

import { Injectable } from '@angular/core';
import { Ecmr } from '@app/shared/models/ecmr/Ecmr';
import { HttpClient } from '@angular/common/http';
import { TemplateUser } from '@app/shared/models/ecmr/TemplateUser';
import { EcmrConsignment } from '@app/shared/models/ecmr/EcmrConsignment';
import { EcmrStatus } from '@app/shared/models/ecmr/EcmrStatus';
import { SealMetadata } from '@app/shared/models/ecmr/SealMetadata';
import { EcmrUrlResolverService } from './ecmr-url-resolver.service';

@Injectable({
    providedIn: 'root',
})
export class EcmrEditorService {
    constructor(
        private http: HttpClient,
        private ecmrUrlResolver: EcmrUrlResolverService
    ) {}

    saveEcmr(ecmr: Ecmr, siteId: string) {
        return this.http.post<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr`,
            ecmr,
            {
                params: { siteId: siteId },
            }
        );
    }

    getEcmr(ecmrId: string) {
        return this.http.get<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${ecmrId}`
        );
    }

    getTemplate(organizationSite: string, templateId: number) {
        return this.http.get<TemplateUser>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/${organizationSite}/template/${templateId}`
        );
    }

    saveTemplate(organizationSite: string, ecmr: Ecmr, name: string) {
        return this.http.post<TemplateUser>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/${organizationSite}/template`,
            ecmr,
            { params: { name: name } }
        );
    }

    updateTemplate(organizationSite: string, template: TemplateUser) {
        return this.http.patch<TemplateUser>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/${organizationSite}/template`,
            template
        );
    }

    updateEcmr(ecmr: Ecmr, siteId: string) {
        return this.http.put<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr`,
            ecmr,
            {
                params: { siteId: siteId },
            }
        );
    }

    sealEcmr(ecmrId: string) {
        return this.http.post<void>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${ecmrId}/seal`,
            null
        );
    }

    getSealMetadata(ecmrId: string) {
        return this.http.get<SealMetadata[]>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${ecmrId}/seal-metadata`
        );
    }

    createEmptyEcmrConsignment(): EcmrConsignment {
        return {
            senderInformation: {
                senderCompanyName: null,
                senderPersonName: null,
                senderStreet: null,
                senderPostcode: null,
                senderCity: null,
                senderCountryCode: {
                    value: null,
                    region: null,
                },
                taxNumber: null,
                senderContactInformation: {
                    email: null,
                    phone: null,
                },
            },
            multiConsigneeShipment: {
                isMultiConsigneeShipment: false,
            },
            consigneeInformation: {
                consigneeCompanyName: null,
                consigneePersonName: null,
                consigneePostcode: null,
                consigneeStreet: null,
                consigneeCity: null,
                consigneeCountryCode: {
                    value: null,
                    region: null,
                },
                consigneeContactInformation: {
                    email: null,
                    phone: null,
                },
            },
            takingOverTheGoods: {
                takingOverTheGoodsPlace: null,
                logisticsTimeOfArrivalDateTime: null,
                logisticsTimeOfDepartureDateTime: null,
            },
            deliveryOfTheGoods: {
                logisticsLocationCity: null,
                logisticsLocationOpeningHours: null,
            },
            sendersInstructions: {
                transportInstructionsDescription: null,
            },
            carrierInformation: {
                carrierCompanyName: null,
                carrierDriverName: null,
                carrierPostcode: null,
                carrierStreet: null,
                carrierCity: null,
                carrierCountryCode: {
                    value: null,
                    region: null,
                },
                taxNumber: null,
                vehicleConfigurationHistory: [],
                carrierContactInformation: {
                    email: null,
                    carrierPhone: null,
                    driverPhone: null,
                },
            },
            successiveCarrierInformation: {
                successiveCarrierCity: null,
                successiveCarrierCountryCode: {
                    value: null,
                    region: null,
                },
                successiveCarrierCompanyName: null,
                successiveCarrierDriverName: null,
                successiveCarrierPostcode: null,
                successiveCarrierStreet: null,
                taxNumber: null,
                vehicleConfigurationHistory: [],
                successiveCarrierContactInformation: {
                    email: null,
                    carrierPhone: null,
                    driverPhone: null,
                },
            },
            carriersReservationsAndObservationsOnTakingOverTheGoods: {
                carrierReservationsObservations: null,
                senderReservationsObservationsSignature: null,
            },
            documentsHandedToCarrier: {
                documentsRemarks: null,
            },
            itemList: [],
            specialPermits: [],
            specialAgreementsSenderCarrier: { customSpecialAgreement: null },
            toBePaidBy: {
                customChargeCarriage: {
                    value: null,
                    currency: null,
                    payer: null,
                },
                customChargeSupplementary: {
                    value: null,
                    currency: null,
                    payer: null,
                },
                customChargeCustomsDuties: {
                    value: null,
                    currency: null,
                    payer: null,
                },
                customChargeOther: {
                    value: null,
                    currency: null,
                    payer: null,
                },
            },
            otherUsefulParticulars: { customParticulars: null },
            cashOnDelivery: { customCashOnDelivery: null },
            established: {
                customEstablishedDate: null,
                customEstablishedIn: null,
            },
            goodsReceived: {
                confirmedLogisticsLocationName: null,
                consigneeReservationsObservations: null,
                consigneeSignature: null,
                consigneeSignatureDate: null,
                consigneeTimeOfArrival: null,
                consigneeTimeOfDeparture: null,
            },
            nonContractualPartReservedForTheCarrier: {
                nonContractualCarrierRemarks: null,
            },
            referenceIdentificationNumber: {
                value: null,
            },
        };
    }

    copyEcmr(ecmr: Ecmr): Ecmr {
        ecmr.ecmrId = null;
        ecmr.ecmrConsignment.goodsReceived.consigneeSignature = null;
        // Reset Carrier Reservation
        ecmr.ecmrConsignment.carriersReservationsAndObservationsOnTakingOverTheGoods.senderReservationsObservationsSignature =
            null;
        ecmr.ecmrConsignment.carriersReservationsAndObservationsOnTakingOverTheGoods.carrierReservationsObservations =
            null;
        ecmr.ecmrConsignment.nonContractualPartReservedForTheCarrier.nonContractualCarrierRemarks =
            null;
        // Reset carrier-entered vehicle configuration (runtime data, must not carry stale plates/timestamps)
        ecmr.ecmrConsignment.carrierInformation.vehicleConfigurationHistory = [];
        ecmr.ecmrConsignment.successiveCarrierInformation.vehicleConfigurationHistory =
            [];
        // Reset Place/Date of the Goods Receiving and Consignee Remarks
        ecmr.ecmrConsignment.goodsReceived.consigneeReservationsObservations =
            null;
        ecmr.ecmrConsignment.goodsReceived.confirmedLogisticsLocationName =
            null;
        ecmr.ecmrConsignment.goodsReceived.consigneeSignatureDate = null;
        // Set new referenceIdentificationNumber to Copy
        ecmr.ecmrConsignment.referenceIdentificationNumber.value += ': Copy';
        //Status
        ecmr.ecmrStatus = EcmrStatus.NEW;
        //Items
        ecmr.ecmrConsignment.itemList = [];

        return ecmr;
    }
}
